import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FileText,
    Plus,
    Edit,
    Trash2,
    CheckCircle,
    X,
    DollarSign,
    TrendingUp,
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
    fetchNoteDeFraisRequest,
    createNoteDeFraisRequest,
    updateNoteDeFraisRequest,
    deleteNoteDeFraisRequest,
} from '../../../../redux/actions/noteDeFraisActions';
import '../parametresStyles/ParamNF.css';

const ParamNF = () => {
    const dispatch = useDispatch();
    const { notesDeFrais, loading, error } = useSelector((state) => state.noteDeFrais || {
        notesDeFrais: [],
        loading: false,
        error: null,
    });

    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [activeTab, setActiveTab] = useState("notes");
    const [formData, setFormData] = useState({
        category: '',
        code: '',
        montantMinimum: '',
        typeJustificatif: '',
        obligatoire: false,
        centreCouts: '',
        workflow: 'manager', // ← AJOUT: valeur par défaut
        actif: true,
    });

    const typeJustificatifOptions = [
        'FACTURE',
        'TICKET',
        'RECU',
        'ATTESTATION',
        'AUCUN',
    ];

    // ← AJOUT: Options de workflow
    const workflowOptions = [
        { value: 'manager', label: 'Manager' },
        { value: 'admin', label: 'Admin RH' },
        { value: 'both', label: 'Les deux' },
    ];

    useEffect(() => {
        dispatch(fetchNoteDeFraisRequest());
    }, [dispatch]);

    const tabs = useMemo(
        () => [
            { id: "notes", label: "Notes de frais", icon: FileText },
            { id: "plafonds", label: "Plafonds", icon: TrendingUp },
        ],
        []
    );

    const openModal = (note = null) => {
        if (note) {
            setEditItem(note);
            setFormData({
                category: note.category || '',
                code: note.code || '',
                montantMinimum: note.montantMinimum || '',
                typeJustificatif: note.typeJustificatif || '',
                obligatoire: note.obligatoire || false,
                centreCouts: note.centreCouts || '',
                workflow: note.workflow || 'manager', // ← AJOUT
                actif: note.actif ?? true,
            });
        } else {
            setEditItem(null);
            setFormData({
                category: '',
                code: '',
                montantMinimum: '',
                typeJustificatif: '',
                obligatoire: false,
                centreCouts: '',
                workflow: 'manager', // ← AJOUT
                actif: true,
            });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditItem(null);
        setFormData({
            category: '',
            code: '',
            montantMinimum: '',
            typeJustificatif: '',
            obligatoire: false,
            centreCouts: '',
            workflow: 'manager', // ← AJOUT
            actif: true,
        });
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.category.trim() || !formData.code.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'La catégorie et le code sont obligatoires',
                confirmButtonColor: '#204385',
            });
            return;
        }

        const payload = {
            ...formData,
            montantMinimum: formData.montantMinimum ? parseFloat(formData.montantMinimum) : null,
        };

        if (editItem) {
            dispatch(updateNoteDeFraisRequest(editItem.id, payload));
        } else {
            dispatch(createNoteDeFraisRequest(payload));
        }
        closeModal();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Confirmation',
            text: 'Voulez-vous vraiment supprimer cette note de frais ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ee645f',
            cancelButtonColor: '#204385',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteNoteDeFraisRequest(id));
            }
        });
    };

    const toggleActif = (note) => {
        dispatch(
            updateNoteDeFraisRequest(note.id, {
                ...note,
                actif: !note.actif,
            })
        );
    };

    // ← AJOUT: Fonction pour afficher le label du workflow
    const getWorkflowLabel = (workflow) => {
        const option = workflowOptions.find(opt => opt.value === workflow);
        return option ? option.label : workflow;
    };

    const renderModal = () => {
        if (!showModal) return null;
        const isEdit = !!editItem;

        return (
            <div className="pcModal">
                <div className="pcModal__panel" role="dialog" aria-modal="true">
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            {isEdit ? 'Modifier une note de frais' : 'Ajouter une note de frais'}
                        </div>
                        <button className="pcIconBtn" onClick={closeModal} type="button" aria-label="Fermer" disabled={loading}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="pcForm">
                            <div className="pcField">
                                <label>Catégorie *</label>
                                <input
                                    type="text"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Code *</label>
                                <input
                                    type="text"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Montant Minimum (DH)</label>
                                <input
                                    type="number"
                                    name="montantMinimum"
                                    value={formData.montantMinimum}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Type de Justificatif</label>
                                <select
                                    name="typeJustificatif"
                                    value={formData.typeJustificatif}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Aucun</option>
                                    {typeJustificatifOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="pcField">
                                <label>Centre de Coûts</label>
                                <input
                                    type="text"
                                    name="centreCouts"
                                    value={formData.centreCouts}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            </div>

                            {/* ← AJOUT: Champ Workflow avec radio buttons */}
                            <div className="pcField">
                                <label>Niveau de validation *</label>
                                <div className="pcRadioGroup">
                                    {workflowOptions.map((option) => (
                                        <label key={option.value} className="pcRadioLabel">
                                            <input
                                                type="radio"
                                                name="workflow"
                                                value={option.value}
                                                checked={formData.workflow === option.value}
                                                onChange={handleChange}
                                                disabled={loading}
                                            />
                                            <span>{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="pcField">
                                <label>Justificatif obligatoire</label>
                                <div className="pcCheckRow">
                                    <input
                                        type="checkbox"
                                        name="obligatoire"
                                        checked={formData.obligatoire}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                    <span>Oui</span>
                                </div>
                            </div>

                            {isEdit && (
                                <div className="pcField">
                                    <label>Actif</label>
                                    <div className="pcCheckRow">
                                        <input
                                            type="checkbox"
                                            name="actif"
                                            checked={formData.actif}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                        <span>Oui</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pcModal__footer">
                            <button className="pcBtn pcBtn--ghost" type="button" onClick={closeModal} disabled={loading}>
                                Annuler
                            </button>
                            <button className="pcBtn pcBtn--primary" type="button" onClick={handleSubmit} disabled={loading}>
                                {isEdit ? 'Enregistrer' : 'Ajouter'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Gestion des notes de frais</h1>
                    <p>Configurez les catégories de frais, montants minimums et justificatifs requis.</p>
                    {error && <div style={{ marginTop: 8, color: '#b91c1c', fontWeight: 700 }}>Erreur: {error}</div>}
                </div>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcTabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`pcTab ${activeTab === tab.id ? "is-active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    type="button"
                                >
                                    <Icon size={16} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="pcBody">
                        {/* NOTES DE FRAIS */}
                        {activeTab === "notes" && (
                            <div>
                                <div className="pcTopRow">
                                    <h2 className="pcTitle">Catégories de frais paramétrables</h2>
                                    <button
                                        className="pcBtn pcBtn--primary"
                                        type="button"
                                        onClick={() => openModal()}
                                        disabled={loading}
                                    >
                                        <Plus size={16} /> Ajouter
                                    </button>
                                </div>

                                <div className="pcTableWrap">
                                    <table className="pcTable">
                                        <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Catégorie</th>
                                            <th>Montant Min.</th>
                                            <th>Type Justificatif</th>
                                            <th>Obligatoire</th>
                                            <th>Centre de Coûts</th>
                                            <th>Niveau validation</th> {/* ← AJOUT */}
                                            <th>Statut</th>
                                            <th className="is-right">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={9} className="pcEmptyCell">
                                                    Chargement...
                                                </td>
                                            </tr>
                                        ) : (notesDeFrais || []).length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="pcEmptyCell">
                                                    Aucune note de frais configurée
                                                </td>
                                            </tr>
                                        ) : (
                                            (notesDeFrais || []).map((note) => (
                                                <tr key={note.id}>
                                                    <td>
                                                        <span className="pcCode" style={{ backgroundColor: '#204385' }}>
                                                            {note.code}
                                                        </span>
                                                    </td>
                                                    <td className="pcStrong">{note.category}</td>
                                                    <td className="pcMuted">
                                                        {note.montantMinimum ? (
                                                            <span className="pcInline">
                                                                {note.montantMinimum.toFixed(2)} DH
                                                            </span>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                    <td>
                                                        {note.typeJustificatif ? (
                                                            <span className="pcInline">
                                                                <FileText size={14} className="pcAccent" />
                                                                {note.typeJustificatif}
                                                            </span>
                                                        ) : (
                                                            <span className="pcMuted">-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {note.obligatoire ? (
                                                            <CheckCircle size={18} className="pcOk" />
                                                        ) : (
                                                            <X size={18} className="pcOff" />
                                                        )}
                                                    </td>
                                                    <td className="pcMuted">{note.centreCouts || '-'}</td>
                                                    {/* ← AJOUT: Affichage du workflow */}
                                                    <td>
                                                        <span className="pcBadge">
                                                            {getWorkflowLabel(note.workflow)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={`pcPill ${note.actif ? 'is-on' : 'is-off'}`}
                                                            type="button"
                                                            onClick={() => toggleActif(note)}
                                                            disabled={loading}
                                                        >
                                                            {note.actif ? 'Actif' : 'Inactif'}
                                                        </button>
                                                    </td>
                                                    <td className="is-right">
                                                        <div className="pcRowActions">
                                                            <button
                                                                className="pcIconBtn"
                                                                type="button"
                                                                title="Modifier"
                                                                onClick={() => openModal(note)}
                                                                disabled={loading}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                            <button
                                                                className="pcIconBtn pcIconBtn--danger"
                                                                type="button"
                                                                title="Supprimer"
                                                                onClick={() => handleDelete(note.id)}
                                                                disabled={loading}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* PLAFONDS */}
                        {activeTab === "plafonds" && (
                            <div>
                                <div className="pcTopRow">
                                    <h2 className="pcTitle">Gestion des plafonds</h2>
                                </div>

                                <div className="pcEmptyBox">
                                    Section des plafonds à venir...
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {renderModal()}
        </div>
    );
};

export default ParamNF;